import { Component, OnInit } from '@angular/core';
import { CurrencyPipe, DatePipe, DecimalPipe, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  Filter,
  LayoutDashboard,
  LucideAngularModule,
  Minus,
  PoundSterling,
  Snowflake,
  TrendingUp,
  Users,
  X,
} from 'lucide-angular';
import { SalesDashboardService } from '../shared/sales-dashboard.service';
import {
  ConversionDimension,
  ConversionRow,
  DatePresetKey,
  FunnelStage,
  MetricTrend,
  SalesDashboardContactRow,
  SalesDashboardContactsResponse,
  SalesDashboardMetrics,
  SalesDashboardOption,
  SalesDashboardQuery,
} from '../shared/sales-dashboard.model';

interface ActiveDrill {
  metric: string;
  stage: string | null;
  title: string;
}

interface DatePreset {
  key: DatePresetKey;
  label: string;
  daysBack: number;
}

/**
 * TVG-236: Sales Dashboard V1.
 *
 * One dashboard for both roles. Every figure is a count (or average) of
 * contacts; quote/event numbers only ever appear as attributes of a contact.
 * The drill-down list is the primary working surface — a genuine worklist, not
 * just a summary table.
 */
@Component({
  selector: 'app-sales-dashboard',
  standalone: true,
  templateUrl: './sales-dashboard.component.html',
  imports: [NgClass, FormsModule, CurrencyPipe, DatePipe, DecimalPipe, LucideAngularModule],
})
export class SalesDashboardComponent implements OnInit {
  protected readonly ChevronDown = ChevronDown;
  protected readonly Filter = Filter;
  protected readonly Users = Users;
  protected readonly PoundSterling = PoundSterling;
  protected readonly Snowflake = Snowflake;
  protected readonly TrendingUp = TrendingUp;
  protected readonly LayoutDashboard = LayoutDashboard;
  protected readonly X = X;
  protected readonly ArrowUp = ArrowUp;
  protected readonly ArrowDown = ArrowDown;
  protected readonly Minus = Minus;

  protected loading = false;
  protected loadingContacts = false;

  protected metrics?: SalesDashboardMetrics;
  protected isManagement = false;
  protected venueOptions: SalesDashboardOption[] = [];
  protected staffOptions: SalesDashboardOption[] = [];

  protected selectedVenueIds = new Set<number>();
  protected selectedStaffIds = new Set<number>();
  protected venueFilterOpen = false;
  protected staffFilterOpen = false;

  protected readonly presets: DatePreset[] = [
    { key: 'today', label: 'Today', daysBack: 0 },
    { key: 'last-2-days', label: 'Last 2 days', daysBack: 1 },
    { key: 'last-3-days', label: 'Last 3 days', daysBack: 2 },
    { key: 'last-5-days', label: 'Last 5 days', daysBack: 4 },
    { key: 'custom', label: 'Custom', daysBack: 0 },
  ];
  protected activePreset: DatePresetKey = 'last-5-days';
  protected customFrom = '';
  protected customTo = '';

  protected groupBy: ConversionDimension = 'venue';
  protected highValueThreshold: number | null = null;
  protected goingColdDays: number | null = null;

  protected activeDrill: ActiveDrill = { metric: 'pipeline', stage: null, title: 'Full pipeline' };
  protected contacts: SalesDashboardContactRow[] = [];
  protected contactsMeta?: SalesDashboardContactsResponse['meta'];
  protected page = 1;
  protected readonly pageSize = 50;

  constructor(
    private readonly service: SalesDashboardService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    const today = this.formatDate(new Date());
    this.customFrom = this.formatDate(this.daysAgo(4));
    this.customTo = today;
    // TVG-236: when returning from a contact opened out of the worklist, restore
    // whatever filters were active rather than resetting to the default view.
    this.restoreStateFromQueryParams();
    this.loadDashboard();
    this.loadContacts();
  }

  // --- filter interactions -------------------------------------------------

  protected toggleVenueFilterDropdown(): void {
    this.venueFilterOpen = !this.venueFilterOpen;
    this.staffFilterOpen = false;
  }

  protected toggleStaffFilterDropdown(): void {
    this.staffFilterOpen = !this.staffFilterOpen;
    this.venueFilterOpen = false;
  }

  protected isVenueSelected(id: number): boolean {
    return this.selectedVenueIds.has(id);
  }

  protected isStaffSelected(id: number): boolean {
    return this.selectedStaffIds.has(id);
  }

  protected toggleVenue(id: number): void {
    this.selectedVenueIds.has(id) ? this.selectedVenueIds.delete(id) : this.selectedVenueIds.add(id);
    this.onFiltersChanged();
  }

  protected toggleStaff(id: number): void {
    this.selectedStaffIds.has(id) ? this.selectedStaffIds.delete(id) : this.selectedStaffIds.add(id);
    this.onFiltersChanged();
  }

  protected clearVenues(): void {
    if (this.selectedVenueIds.size === 0) {
      return;
    }
    this.selectedVenueIds.clear();
    this.onFiltersChanged();
  }

  protected clearStaff(): void {
    if (this.selectedStaffIds.size === 0) {
      return;
    }
    this.selectedStaffIds.clear();
    this.onFiltersChanged();
  }

  protected venueFilterLabel(): string {
    if (this.selectedVenueIds.size === 0) {
      return this.isManagement ? 'All venues' : 'My venues';
    }
    return `${this.selectedVenueIds.size} venue${this.selectedVenueIds.size === 1 ? '' : 's'}`;
  }

  protected staffFilterLabel(): string {
    if (this.selectedStaffIds.size === 0) {
      return 'All salespeople';
    }
    return `${this.selectedStaffIds.size} selected`;
  }

  protected selectPreset(key: DatePresetKey): void {
    this.activePreset = key;
    this.onFiltersChanged();
  }

  protected applyCustomRange(): void {
    this.activePreset = 'custom';
    this.onFiltersChanged();
  }

  protected setGroupBy(dimension: ConversionDimension): void {
    if (this.groupBy === dimension) {
      return;
    }
    this.groupBy = dimension;
    this.loadDashboard();
  }

  protected applyThresholds(): void {
    this.onFiltersChanged();
  }

  // --- drill-down ----------------------------------------------------------

  protected selectDrill(metric: string, stage: string | null, title: string): void {
    this.activeDrill = { metric, stage, title };
    this.page = 1;
    this.loadContacts();
  }

  protected drillFunnel(stage: FunnelStage): void {
    this.selectDrill('funnel', stage.stage, `${stage.label} contacts`);
  }

  protected isActiveDrill(metric: string, stage: string | null = null): boolean {
    return this.activeDrill.metric === metric && this.activeDrill.stage === stage;
  }

  /** Reset the worklist back to the full pipeline view. */
  protected clearDrill(): void {
    this.selectDrill('pipeline', null, 'Full pipeline');
  }

  /**
   * Navigate to the contact detail page, carrying the active filter state as
   * query params so the contact page can offer a "Back to Dashboard" link that
   * restores this exact view.
   */
  protected navigateToContact(contactId: number): void {
    void this.router.navigate(['/contacts', contactId], {
      queryParams: { returnTo: 'sales-dashboard', ...this.filterQueryParams() },
    });
  }

  /**
   * Tailwind classes for stage badge colouring, keyed on the internal stage
   * value returned by {@see InternalSalesStage}.
   */
  protected stageBadgeClass(stage: string): string {
    const map: Record<string, string> = {
      pre_quote: 'bg-gray-100 text-gray-600 dark:bg-white/[0.06] dark:text-gray-300',
      quote_received: 'bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300',
      active_opportunity: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300',
      viewing_booked: 'bg-violet-50 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300',
      pencil_provisional: 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
      confirmed: 'bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-300',
      completed: 'bg-teal-50 text-teal-700 dark:bg-teal-500/15 dark:text-teal-300',
      lost: 'bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-300',
    };
    return map[stage] ?? 'bg-gray-100 text-gray-600 dark:bg-white/[0.06] dark:text-gray-300';
  }

  // --- funnel chart --------------------------------------------------------

  /**
   * Width of a funnel bar as a percentage of the widest stage, so the section
   * reads as a real funnel/bar chart rather than five identical boxes. Every
   * stage keeps a small floor so an empty stage is still visible and clickable.
   */
  protected funnelBarWidth(stage: FunnelStage): number {
    const max = Math.max(1, ...(this.metrics?.funnel.map((s) => s.contacts) ?? [0]));
    const ratio = stage.contacts / max;
    return Math.round((0.08 + ratio * 0.92) * 100);
  }

  /** Solid fill colour for a funnel bar, keyed on the stage. */
  protected funnelBarClass(stage: string): string {
    const map: Record<string, string> = {
      pre_quote: 'bg-slate-400 dark:bg-slate-500',
      quote_received: 'bg-blue-500 dark:bg-blue-500',
      active_opportunity: 'bg-indigo-500 dark:bg-indigo-500',
      viewing_booked: 'bg-violet-500 dark:bg-violet-500',
      pencil_provisional: 'bg-amber-500 dark:bg-amber-500',
    };
    return map[stage] ?? 'bg-brand-500';
  }

  // --- conversion chart ----------------------------------------------------

  /**
   * The three conversion steps of a row as chart-ready rows: label, rate and
   * bar colour. Drives the mini bar chart that replaced the percentage table.
   *
   * @return array of the three stage-to-stage steps in journey order.
   */
  protected conversionSteps(row: ConversionRow): { label: string; rate: number | null; barClass: string }[] {
    return [
      { label: 'Quote → Viewing', rate: row.quote_to_viewing, barClass: 'bg-blue-500' },
      { label: 'Viewing → Pencil', rate: row.viewing_to_pencil, barClass: 'bg-violet-500' },
      { label: 'Pencil → Confirmed', rate: row.pencil_to_confirmed, barClass: 'bg-emerald-500' },
    ];
  }

  // --- trend indicators ----------------------------------------------------

  protected trendIcon(trend: MetricTrend) {
    return trend.direction === 'up' ? this.ArrowUp : trend.direction === 'down' ? this.ArrowDown : this.Minus;
  }

  /**
   * Colour a trend chip by whether the movement is good news. For most cards a
   * rise is positive; for "going cold" a rise is a bad sign, so `positiveIsUp`
   * is flipped there.
   */
  protected trendClass(trend: MetricTrend, positiveIsUp = true): string {
    if (trend.direction === 'flat') {
      return 'text-gray-400 dark:text-gray-500';
    }
    const isGood = trend.direction === 'up' ? positiveIsUp : !positiveIsUp;
    return isGood
      ? 'text-emerald-600 dark:text-emerald-400'
      : 'text-rose-600 dark:text-rose-400';
  }

  /** "+12%", "−4%" or "—" when there is no comparable baseline. */
  protected trendLabel(trend: MetricTrend): string {
    if (trend.change === null) {
      return trend.direction === 'up' ? 'New' : '—';
    }
    const rounded = Math.abs(Math.round(trend.change));
    const sign = trend.change > 0 ? '+' : trend.change < 0 ? '−' : '';
    return `${sign}${rounded}%`;
  }

  protected totalPages(): number {
    if (!this.contactsMeta || this.contactsMeta.total === 0) {
      return 1;
    }
    return Math.ceil(this.contactsMeta.total / this.contactsMeta.page_size);
  }

  protected goToPage(page: number): void {
    if (page < 1 || page > this.totalPages() || page === this.page) {
      return;
    }
    this.page = page;
    this.loadContacts();
  }

  // --- data loading --------------------------------------------------------

  private onFiltersChanged(): void {
    this.page = 1;
    this.loadDashboard();
    this.loadContacts();
  }

  private loadDashboard(): void {
    this.loading = true;
    this.service.getDashboard(this.currentQuery()).subscribe({
      next: (res) => {
        this.metrics = res.metrics;
        this.venueOptions = res.options.venues;
        this.staffOptions = res.options.staff;
        this.isManagement = res.is_management;
        if (this.highValueThreshold === null) {
          this.highValueThreshold = res.filters.high_value_threshold;
        }
        if (this.goingColdDays === null) {
          this.goingColdDays = res.filters.going_cold_days;
        }
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  private loadContacts(): void {
    this.loadingContacts = true;
    this.service
      .getContacts(this.currentQuery(), this.activeDrill.metric, this.activeDrill.stage, this.page, this.pageSize)
      .subscribe({
        next: (res) => {
          this.contacts = res.data;
          this.contactsMeta = res.meta;
          this.loadingContacts = false;
        },
        error: () => {
          this.loadingContacts = false;
        },
      });
  }

  // --- filter persistence across the contact drill-through ----------------

  /**
   * A flat, URL-friendly snapshot of the active filters and drill selection.
   * Carried to the contact page and handed straight back so returning restores
   * this exact view. Empty values are omitted to keep the URL tidy.
   */
  private filterQueryParams(): Params {
    const params: Params = {
      sd_metric: this.activeDrill.metric,
      sd_title: this.activeDrill.title,
      sd_preset: this.activePreset,
      sd_group: this.groupBy,
    };

    if (this.activeDrill.stage) {
      params['sd_stage'] = this.activeDrill.stage;
    }
    if (this.selectedVenueIds.size) {
      params['sd_venues'] = Array.from(this.selectedVenueIds).join(',');
    }
    if (this.selectedStaffIds.size) {
      params['sd_staff'] = Array.from(this.selectedStaffIds).join(',');
    }
    if (this.activePreset === 'custom') {
      params['sd_from'] = this.customFrom;
      params['sd_to'] = this.customTo;
    }
    if (this.highValueThreshold !== null) {
      params['sd_hv'] = this.highValueThreshold;
    }
    if (this.goingColdDays !== null) {
      params['sd_cold'] = this.goingColdDays;
    }

    return params;
  }

  /** Rehydrate the filter state from `sd_*` query params, if present. */
  private restoreStateFromQueryParams(): void {
    const params = this.route.snapshot.queryParams;
    if (!params['sd_metric']) {
      return;
    }

    this.activeDrill = {
      metric: params['sd_metric'],
      stage: params['sd_stage'] ?? null,
      title: params['sd_title'] ?? 'Full pipeline',
    };

    this.selectedVenueIds = this.parseIdSet(params['sd_venues']);
    this.selectedStaffIds = this.parseIdSet(params['sd_staff']);

    if (this.isPresetKey(params['sd_preset'])) {
      this.activePreset = params['sd_preset'];
    }
    if (params['sd_from']) {
      this.customFrom = params['sd_from'];
    }
    if (params['sd_to']) {
      this.customTo = params['sd_to'];
    }
    if (params['sd_group'] === 'venue' || params['sd_group'] === 'salesperson') {
      this.groupBy = params['sd_group'];
    }
    if (params['sd_hv'] !== undefined && params['sd_hv'] !== '') {
      this.highValueThreshold = Number(params['sd_hv']);
    }
    if (params['sd_cold'] !== undefined && params['sd_cold'] !== '') {
      this.goingColdDays = Number(params['sd_cold']);
    }
  }

  private parseIdSet(value: string | undefined): Set<number> {
    if (!value) {
      return new Set<number>();
    }
    return new Set(
      value
        .split(',')
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id)),
    );
  }

  private isPresetKey(value: unknown): value is DatePresetKey {
    return typeof value === 'string' && this.presets.some((preset) => preset.key === value);
  }

  private currentQuery(): SalesDashboardQuery {
    const range = this.computeRange();
    return {
      venueIds: Array.from(this.selectedVenueIds),
      staffIds: Array.from(this.selectedStaffIds),
      from: range.from,
      to: range.to,
      highValueThreshold: this.highValueThreshold,
      goingColdDays: this.goingColdDays,
      groupBy: this.groupBy,
    };
  }

  private computeRange(): { from: string; to: string } {
    if (this.activePreset === 'custom') {
      return {
        from: this.customFrom || this.formatDate(this.daysAgo(4)),
        to: this.customTo || this.formatDate(new Date()),
      };
    }
    const preset = this.presets.find((p) => p.key === this.activePreset);
    const back = preset ? preset.daysBack : 4;
    return { from: this.formatDate(this.daysAgo(back)), to: this.formatDate(new Date()) };
  }

  private daysAgo(days: number): Date {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date;
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
